import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import Seo from '@/components/base/Seo';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import InsightExplorer from '@/pages/insight/components/InsightExplorer';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, getCategoryBySlug } from '@/lib/content';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;

  if (!category) {
    return (
      <PageShell>
        <Seo
          title="未找到该主题分类 - 职镜观察"
          description="你访问的主题分类不存在，可以返回内幕观察列表，或从首页选择其他主题入口。"
        />
        <div className="mx-auto max-w-content px-4 py-24 text-center md:px-6">
          <i className="ri-folder-unknow-line text-4xl text-foreground-300" />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这个分类不存在</h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            分类地址可能已经调整，你可以从下面重新选择感兴趣的主题。
          </p>
          <div className="mx-auto mt-7 flex max-w-2xl flex-wrap justify-center gap-2">
            {categories.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className="rounded-full border border-background-300 px-4 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </PageShell>
    );
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const list = getArticlesByCategory(category.slug);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: '内幕观察', item: `${origin}/insight` },
        { '@type': 'ListItem', position: 3, name: category.name, item: `${origin}/category/${category.slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${category.name} - 职镜观察`,
      description: category.description,
      url: `${origin}/category/${category.slug}`,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: list.map((article, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: article.title,
          url: `${origin}/insight/${article.id}`,
        })),
      },
    },
  ];

  return (
    <PageShell>
      <Seo
        title={`${category.name} | ${category.short} - 职镜观察`}
        description={category.description}
        keywords={`${category.name},${category.short},职场观察,职镜观察`}
        image={category.cover}
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100 paper-grain">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs
            items={[
              { name: '首页', path: '/' },
              { name: '内幕观察', path: '/insight' },
              { name: category.name },
            ]}
          />
          <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${category.icon} text-xl`} />
                </span>
                <span className="font-label text-xs tracking-[0.28em] text-primary-600">
                  {list.length} 条观察
                </span>
              </div>
              <h1 className="mt-5 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                {category.name}
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {category.description}
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="h-48 w-full overflow-hidden rounded-lg md:h-60">
                <img
                  src={category.cover}
                  alt={`${category.name}主题配图`}
                  title={`${category.name} - 职场观察主题分类`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto flex max-w-content flex-wrap gap-2 px-4 py-5 md:px-6">
          <span className="mr-1 self-center font-label text-xs text-foreground-500">其他主题</span>
          {categories.map((item) => (
            <Link
              key={item.slug}
              to={`/category/${item.slug}`}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 font-label text-sm transition-colors ${
                item.slug === category.slug
                  ? 'border-primary-500 bg-primary-500 text-background-50'
                  : 'border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700'
              }`}
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
        <InsightExplorer initialCategory={category.slug} showCategoryFilter={false} />
      </div>
    </PageShell>
  );
}